import React, { useCallback, useId, useMemo } from 'react';
import { inputSizes, inputVariants } from 'src/tef/components/config/variantConfig';
import { useIcon } from 'src/tef/components/hooks/useIcon';
import { useResponsiveVariant } from 'src/tef/components/hooks/useResponsiveVariant';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import { getBorderRadiusBySize } from 'src/tef/components/utils/getBorderRadius';
import { containsMaliciousPatterns, sanitizeInputMultiline } from 'src/tef/components/utils/security';
import type { ComponentVariant, ForwardedHTMLProps } from 'src/tef/components/utils/types';
import type { IconProp } from 'src/tef/components/utils/types';
import { auditLogger } from 'src/utils/security';

type ResizeOption = 'none' | 'vertical' | 'horizontal' | 'both';

interface TextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'>, ForwardedHTMLProps {
  variant?: ComponentVariant;
  icon?: IconProp;
  error?: boolean;
  loading?: boolean;
  resize?: ResizeOption;
  minRows?: number;
  maxRows?: number;
  showCharCount?: boolean;
  maxLength?: number;
  className?: string;
}

const resizeClasses: Record<ResizeOption, string> = {
  none: 'resize-none',
  vertical: 'resize-y',
  horizontal: 'resize-x',
  both: 'resize',
};

export const TextArea = React.memo(
  React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
    (
      {
        variant = {},
        icon,
        error = false,
        loading = false,
        resize = 'vertical',
        minRows = 3,
        maxRows,
        showCharCount = false,
        maxLength,
        className,
        onChange,
        value,
        disabled,
        ...props
      },
      ref
    ) => {
      const tef = useTEF();
      const uniqueId = useId();
      const charCountId = `char-count-${uniqueId}`;
      const forwardedProps = forwardProps(props);
      const iconElement = useIcon(icon);

      const variantSize = variant.size || 'md';
      const responsiveSize = useResponsiveVariant(variantSize);
      const resolvedSize = typeof responsiveSize === 'string' ? responsiveSize : 'md';
      const sizeConfig = typeof resolvedSize === 'string' ? inputSizes[resolvedSize] : inputSizes.md;

      const hasIcon = !!icon;
      const borderRadius = getBorderRadiusBySize(resolvedSize, tef);
      const isDisabled = disabled || loading;

      const charCount = typeof value === 'string' ? value.length : 0;
      const isOverLimit = maxLength ? charCount > maxLength : false;

      const textareaClasses = useMemo(() => {
        return cn(
          'w-full',
          sizeConfig.padding,
          sizeConfig.text,
          borderRadius,
          'border',
          error || isOverLimit ? inputVariants.default.error : inputVariants.default.base,
          error || isOverLimit ? inputVariants.default.error : inputVariants.default.focus,
          'bg-white dark:bg-slate-800',
          tef.colors.inputText,
          'transition-all duration-200',
          'focus:outline-none',
          resizeClasses[resize],
          hasIcon && inputVariants.withLeftIcon.base,
          isDisabled && 'opacity-50 cursor-not-allowed',
          loading && 'animate-pulse',
          className
        );
      }, [
        sizeConfig,
        error,
        isOverLimit,
        hasIcon,
        borderRadius,
        resize,
        isDisabled,
        loading,
        className,
        tef.colors.inputText,
      ]);

      const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
          const rawValue = e.target.value;
          const sanitized = sanitizeInputMultiline(rawValue);

          if (containsMaliciousPatterns(sanitized)) {
            auditLogger.log('MALICIOUS_INPUT_BLOCKED', {
              component: 'TextArea',
              inputLength: rawValue.length,
            });
            return;
          }

          if (onChange) {
            const target = e.target as HTMLTextAreaElement;
            const syntheticTarget = { ...target, value: sanitized, name: target.name };
            const syntheticEvent = {
              ...e,
              target: syntheticTarget,
              currentTarget: { ...e.currentTarget, value: sanitized, name: target.name },
            } as React.ChangeEvent<HTMLTextAreaElement>;

            onChange(syntheticEvent);
          }
        },
        [onChange]
      );

      const rowsStyle = useMemo(() => {
        const styles: React.CSSProperties = {};
        if (minRows) {
          styles.minHeight = `${minRows * 1.5}em`;
        }
        if (maxRows) {
          styles.maxHeight = `${maxRows * 1.5}em`;
        }
        return styles;
      }, [minRows, maxRows]);

      return (
        <section className="relative">
          {hasIcon && (
            <span className={cn('absolute top-3 pointer-events-none', sizeConfig.iconOffset)} aria-hidden="true">
              <span className={cn(sizeConfig.icon, 'block', tef.colors.muted)}>{iconElement}</span>
            </span>
          )}

          <textarea
            ref={ref}
            value={value}
            onChange={handleChange}
            disabled={isDisabled}
            maxLength={maxLength}
            rows={minRows}
            style={rowsStyle}
            className={textareaClasses}
            aria-invalid={error || isOverLimit}
            aria-describedby={showCharCount ? charCountId : undefined}
            {...forwardedProps}
            {...props}
          />

          {showCharCount && (
            <span
              id={charCountId}
              className={cn(
                'absolute bottom-2 right-3',
                tef.typography.textXs,
                isOverLimit ? 'text-red-500' : tef.colors.foregroundTertiary
              )}
            >
              {charCount}
              {maxLength ? `/${maxLength}` : ''}
            </span>
          )}
        </section>
      );
    }
  )
);

TextArea.displayName = 'TextArea';

export default TextArea;
